// @ts-expect-error: @rails/request.js is not typed
import { FetchRequest } from '@rails/request.js'
import { CheckIcon } from 'lucide-react'
import { useState } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'

import { useWindowLocation } from 'utils/windowLocation'

import { type PartnerPayoutAccount } from './types'
import { AddressFields } from '../reusable/form/AddressFields'
import { FormSelectField, FormTextField } from '../reusable/form/FormFields'

type PayoutAccountDetails = {
  TIN: string;
  account_holder_email: string;
  account_holder_first_name: string;
  account_holder_last_name: string;
  business_legal_name: string;
  account_number: string;
  account_type: 'individual' | 'company';
  bank_account_type: 'checking' | 'savings';
  address_line1: string;
  address_line2: string;
  city: string;
  routing_number: string;
  state: string;
  zip: string;
}

export function PayoutAccountDetailsForm ({
  partnerPayoutAccount,
  isInitialSetup = false,
  mapboxAccessToken,
  setShowEditScreen,
}: {
  partnerPayoutAccount?: PartnerPayoutAccount;
  isInitialSetup?: boolean;
  mapboxAccessToken: string;
  setShowEditScreen: (arg: boolean) => void;
}) {
  const { assign } = useWindowLocation()

  const [submissionError, setSubmissionError] = useState<string | undefined>()
  const [submitting, setSubmitting] = useState(false)

  const {
    register,
    unregister,
    getValues,
    handleSubmit,
    setValue,
    watch,
    formState: { dirtyFields, errors },
  } = useForm<PayoutAccountDetails>({
    defaultValues: {
      account_type: partnerPayoutAccount?.account_type || 'company',
      account_holder_first_name:
        partnerPayoutAccount?.account_holder_first_name || '',
      account_holder_last_name:
        partnerPayoutAccount?.account_holder_last_name || '',
      business_legal_name: partnerPayoutAccount?.business_legal_name || '',
      account_holder_email: partnerPayoutAccount?.account_holder_email || '',
      TIN: partnerPayoutAccount?.TIN || '',
      bank_account_type: partnerPayoutAccount?.bank_account_type || 'checking',
      account_number: partnerPayoutAccount?.account_number || '',
      routing_number: partnerPayoutAccount?.routing_number || '',
      address_line1: partnerPayoutAccount?.address_line1 || '',
      address_line2: partnerPayoutAccount?.address_line2 || '',
      city: partnerPayoutAccount?.city || '',
      state: partnerPayoutAccount?.state || '',
      zip: partnerPayoutAccount?.zip || '',
    },
  })

  const accountType = watch('account_type')

  const onSubmit: SubmitHandler<PayoutAccountDetails> = async (
    data: PayoutAccountDetails & Record<string, any>
  ) => {
    setSubmitting(true)
    let method = 'post'
    let dataToSend: Partial<PayoutAccountDetails> = data

    if (partnerPayoutAccount) {
      method = 'patch'
      dataToSend = Object.keys(dirtyFields).reduce((acc, field) => {
        if (field in data) {
          acc[field] = data[field]
        }
        return acc
      }, {} as Partial<PayoutAccountDetails & Record<string, any>>)
      // address_line1 is not a typical react-hook-form field, need to handle it separately
      if (data.address_line1 !== partnerPayoutAccount.address_line1) {
        dataToSend.address_line1 = data.address_line1
      }
    }

    if (dataToSend.account_type === 'individual') {
      dataToSend.business_legal_name = ''
    } else if (dataToSend.account_type === 'company') {
      dataToSend.account_holder_first_name = ''
      dataToSend.account_holder_last_name = ''
    }
    try {
      // TODO: this method of making an API call is deprecated -- please use reactquery instead
    // see https://www.notion.so/thatchhealth/Using-React-Query-for-Fetching-Data-160114874bc280379918d43442405589
      const request = new FetchRequest(
        method,
        '/api/broker/settings/partner_payout_details',
        {
          body: {
            ...dataToSend,
            is_initial_setup: isInitialSetup,
          },
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        }
      )
      const response = await request.perform()
      if (response.ok) {
        const data = await response.json
        assign(data.redirect_to)
      } else if (response.statusCode === 401) {
        setSubmissionError(
          'Your session expired. Please refresh the page and sign in again to continue.'
        )
      } else if (response.statusCode === 422) {
        const errorData = await response.json
        const errors = errorData.errors.join(', ')
        setSubmissionError(
          `There was an error updating payout details. Please correct the errors and try again: ${errors}`
        )
      } else {
        const errorData = await response.json
        throw errorData.errors
      }
    } catch (error: any) {
      console.error('Error updating payout details: ', error)
      setSubmissionError(
        'There was an error updating payout details. Please contact support.'
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      {submissionError && (
        <div
          className='bg-red-100 border border-red-400 text-red-700 p-2 mb-4 rounded relative text-center text-xs'
          role='alert'
        >
          {submissionError}
        </div>
      )}
      <form
        className='flex flex-col justify-between gap-y-6 mx-auto'
        onSubmit={handleSubmit(onSubmit)}
      >
        <p className='text-sm text-gray-700 rounded-md border border-gray-300 bg-gray-200 p-4'>
          We will use the information you provide to arrange your monthly
          payouts for referral fees. Please ensure the information is accurate
          to prevent any delays in receiving your payments.
        </p>

        <h2 className='h2'>Account holder information</h2>
        <div className='flex gap-4 justify-between'>
          <FormSelectField<PayoutAccountDetails>
            disabled={submitting}
            error={errors.account_type}
            fieldId='account_type'
            fieldLabel='Account type'
            fieldOptions={[
              { label: 'Individual', value: 'individual' },
              { label: 'Company', value: 'company' },
            ]}
            register={register}
            validationRule={{
              required: 'Account holder type is required.',
            }}
          />
          {accountType === 'company'
            ? (
              <FormTextField<PayoutAccountDetails>
                disabled={submitting}
                error={errors.business_legal_name}
                fieldId='business_legal_name'
                fieldLabel='Business legal name'
                register={register}
                validationRule={{
                  required: 'Business legal name is required.',
                }}
              />
              )
            : (
              <div className='flex flex-col w-full' />
              )}
        </div>
        {accountType === 'individual' && (
          <div className='flex gap-4 justify-between'>
            <FormTextField<PayoutAccountDetails>
              disabled={submitting}
              error={errors.account_holder_first_name}
              fieldId='account_holder_first_name'
              fieldLabel='Account holder first name'
              register={register}
              validationRule={{
                required: 'Account holder first name is required.',
              }}
            />
            <FormTextField<PayoutAccountDetails>
              disabled={submitting}
              error={errors.account_holder_last_name}
              fieldId='account_holder_last_name'
              fieldLabel='Account holder last name'
              register={register}
              validationRule={{
                required: 'Account holder last name is required.',
              }}
            />
          </div>
        )}
        <div className='flex gap-4 justify-between'>
          <FormTextField<PayoutAccountDetails>
            disabled={submitting}
            error={errors.TIN}
            fieldId='TIN'
            fieldLabel={
              accountType === 'company'
                ? 'Employer identification number'
                : 'Social security number'
            }
            register={register}
            validationRule={{
              required: `${
                accountType === 'company'
                  ? 'Employer identification number'
                  : 'Social security number'
              } is required.`,
            }}
          />
          <FormTextField<PayoutAccountDetails>
            disabled={submitting}
            error={errors.account_holder_email}
            fieldId='account_holder_email'
            fieldLabel='Account holder email'
            register={register}
            validationRule={{
              required: 'Account holder email is required.',
            }}
          />
        </div>
        <h2 className='h2'>Account holder address</h2>
        <AddressFields<PayoutAccountDetails>
          accessToken={mapboxAccessToken}
          disabled={submitting}
          errors={errors}
          getValues={getValues}
          setValue={setValue}
          register={register}
          unregister={unregister}
          requiredFields={['address_line1', 'city', 'state', 'zip']}
        />
        <h2 className='h2'>Bank account information</h2>
        <div className='flex gap-4 justify-between'>
          <FormSelectField<PayoutAccountDetails>
            disabled={submitting}
            error={errors.bank_account_type}
            fieldId='bank_account_type'
            fieldLabel='Bank account type'
            fieldOptions={[
              { label: 'Checking', value: 'checking' },
              { label: 'Savings', value: 'savings' },
            ]}
            register={register}
            validationRule={{
              required: 'Bank account type is required.',
            }}
          />
          <div className='flex flex-col w-full' />
        </div>
        <div className='flex gap-4 justify-between'>
          <FormTextField<PayoutAccountDetails>
            disabled={submitting}
            error={errors.account_number}
            fieldId='account_number'
            fieldLabel='ACH account number'
            register={register}
            validationRule={{
              required: 'ACH account number is required.',
            }}
          />
          <FormTextField<PayoutAccountDetails>
            disabled={submitting}
            error={errors.routing_number}
            fieldId='routing_number'
            fieldLabel='ACH routing number'
            register={register}
            validationRule={{
              required: 'ACH routing number is required.',
            }}
          />
        </div>

        <div className='flex justify-end gap-x-4'>
          <button className='button w-auto' disabled={submitting} type='submit'>
            {submitting
              ? (
                <>
                  <svg
                    className='animate-spin -ml-1 mr-3 h-5 w-5 text-gray-400'
                    xmlns='http://www.w3.org/2000/svg'
                    fill='none'
                    viewBox='0 0 24 24'
                  >
                    <circle
                      className='opacity-25'
                      cx='12'
                      cy='12'
                      r='10'
                      stroke='currentColor'
                      strokeWidth='4'
                    />
                    <path
                      className='opacity-75'
                      fill='currentColor'
                      d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                    />
                  </svg>
                  Saving...
                </>
                )
              : (
                  'Save'
                )}
          </button>
          <button
            className='button-outline w-auto'
            disabled={submitting}
            onClick={(e) => {
              e.preventDefault()
              setShowEditScreen(false)
            }}
          >
            Cancel
          </button>
        </div>
      </form>
    </>
  )
}

export default function PayoutAccountDetailsPage ({
  partnerPayoutAccount,
  isInitialSetup = false,
  mapboxAccessToken,
}: {
  partnerPayoutAccount?: PartnerPayoutAccount;
  isInitialSetup?: boolean;
  mapboxAccessToken: string;
}) {
  const [showEditScreen, setShowEditScreen] = useState(false)
  return (
    <>
      {partnerPayoutAccount && !showEditScreen && (
        <div
          data-testid='payout-details'
          className='flex justify-between items-end gap-x-6 text-gray-700 rounded-md border border-gray-300 p-4'
        >
          <div style={{ flex: 2 }}>
            <h2 className='h2 mb-4 flex gap-x-2 items-center'>
              <CheckIcon
                size={20}
                className='bg-green-700 text-white rounded'
              />
              {partnerPayoutAccount.bank_account_type === 'checking'
                ? 'Checking'
                : 'Savings'}{' '}
              account linked
            </h2>
            <p className='text-sm'>
              <span className='mr-1'>Account number:</span>
              <span className='text-thatch-purple-primary'>
                {partnerPayoutAccount.account_number}
              </span>
            </p>
            <p className='text-sm'>
              <span className='mr-1'>Routing number:</span>
              <span className='text-thatch-purple-primary'>
                {partnerPayoutAccount.routing_number}
              </span>
            </p>
          </div>
          <button
            className='button-outline w-auto'
            onClick={(e) => {
              e.preventDefault()
              setShowEditScreen(true)
            }}
          >
            Edit bank account details
          </button>
        </div>
      )}
      {(!partnerPayoutAccount || showEditScreen) && (
        <PayoutAccountDetailsForm
          partnerPayoutAccount={partnerPayoutAccount}
          isInitialSetup={isInitialSetup}
          mapboxAccessToken={mapboxAccessToken}
          setShowEditScreen={setShowEditScreen}
        />
      )}
    </>
  )
}
